import toast from 'react-hot-toast';
import FileUploadHelper from 'services/file-upload/FileUploadService';
import { generateUUID } from 'utils';
import _ from 'lodash';

export const onAddFiles = ({ files = [], setFiles = () => {}, newFiles = [] }) => {
  const updatedFiles = Array.from(newFiles).map((item) => {
    return {
      id: generateUUID(),
      file: item,
      uploaded: false,
      uploading: true,
      inError: false,
    };
  });
  const filesData = files?.concat(updatedFiles);

  setFiles(filesData);
  return { updatedFiles, allFiles: filesData };
};

export const onUpdateFile = ({ files, setFiles = () => {}, file = {}, newFile = {}, getUrlApi }) => {
  setFiles((updatedFiles = []) => {
    const filesData = _.cloneDeep([...(getUrlApi ? files : updatedFiles)]);
    for (let item of filesData) {
      if (file.id == item.id) {
        item = Object.assign(item, newFile);
      }
    }
    return filesData;
  });
};

export const handleDownloadAttachment = async ({ attachment = '', fileName = '' }) => {
  try {
    const response = await fetch(attachment);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading the attachment:', error);
  }
};

export const handleAttachmentFileChange = ({
  event,
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
  maxSizeInBytes = 15 * 1024 * 1024, // 15 MB
  maxCount = null,
  currentCount = 0,
  onSuccess = () => {},
}) => {
  const newFiles = event.target.files;
  validateAttachmentFiles({ newFiles, allowedTypes, maxSizeInBytes, maxCount, currentCount, onSuccess });
  event.target.value = '';
};

export const validateAttachmentFiles = ({
  newFiles,
  allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'],
  maxSizeInBytes = 15 * 1024 * 1024,
  maxCount = null,
  currentCount = 0,
  onSuccess = () => {},
}) => {
  if (newFiles && newFiles.length > 0) {
    let validFiles = [];

    for (const file of newFiles) {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`File "${file.name}" is not an allowed file type.`);
        continue;
      }
      if (file.size > maxSizeInBytes) {
        toast.error(`File "${file.name}" exceeds the 15 MB size limit.`);
        continue;
      }
      validFiles.push(file);
    }

    if (maxCount !== null && validFiles.length > 0) {
      const totalCount = currentCount + validFiles.length;

      if (totalCount > maxCount) {
        const allowedNewFiles = maxCount - currentCount;

        if (allowedNewFiles <= 0) {
          toast.error(
            `Maximum ${maxCount} file${maxCount > 1 ? 's' : ''} allowed. Please remove existing files first.`,
          );
          return;
        }

        toast.error(
          `Maximum ${maxCount} file${maxCount > 1 ? 's' : ''} allowed. Only ${allowedNewFiles} file${allowedNewFiles > 1 ? 's' : ''} will be added.`,
        );
        validFiles = validFiles.slice(0, allowedNewFiles);
      }
    }

    if (validFiles.length > 0) {
      onSuccess(validFiles);
    }
  }
};

export const uploadFile = async ({
  item = {},
  attachmentKey = '',
  getUrlApi = null,
  files = [],
  setFiles = () => {},
  makeBlob = true,
  isSvg = false,
  typeSlug = '',
  labelSlug = '',
  labelSourceClass = '',
}) => {
  const { file, uploaded } = item;
  if (uploaded) return;

  // Create local preview upfront for all cases
  const createLocalPreview = () => {
    if (makeBlob) {
      return URL.createObjectURL(file);
    } else if (isSvg) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(file);
      });
    }
    return null;
  };

  const localPreview = isSvg ? await createLocalPreview() : createLocalPreview();

  const fileUploader = new FileUploadHelper();
  let presignedUrl = '';

  try {
    presignedUrl = await fileUploader.getPresignedUrlFile({
      attachmentKey,
      labelSlug,
      labelSourceClass,
      typeSlug: typeSlug,
      getUrlApi,
      fileName: file?.name,
      fileSize: file?.size,
    });
  } catch (e) {
    const data = {
      id: item.id,
      uploaded: false,
      uploading: false,
      inError: true,
      localPreview,
    };
    onUpdateFile({ files, setFiles, file: item, newFile: data, getUrlApi });
    return;
  }

  // Try to upload file to S3
  try {
    const res = await fileUploader.uploadFileOnS3({ file, s3PresignUrl: presignedUrl });

    if (res) {
      const data = {
        id: presignedUrl.id,
        uploaded: true,
        uploading: false,
        inError: false,
        localPreview,
      };
      onUpdateFile({ files, setFiles, file: item, newFile: data, getUrlApi });
    } else {
      const data = {
        id: presignedUrl.id,
        uploaded: false,
        uploading: false,
        inError: true,
        localPreview,
      };
      onUpdateFile({ files, setFiles, file: item, newFile: data, getUrlApi });
    }
  } catch (e) {
    const data = {
      id: item.id,
      uploaded: false,
      uploading: false,
      inError: true,
      localPreview,
    };
    onUpdateFile({ files, setFiles, file: item, newFile: data, getUrlApi });
  }
};

export const checkAttachmentsUploading = ({ attachments = [] }) => {
  return attachments?.some((att) => att.uploading === true) ?? false;
};
